import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import Tab from '@mui/material/Tab'
import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import { useTranslation } from 'react-i18next'
import { Box } from '@mui/material'

import { buildRowTestId } from '@hapana/shared/web/utils/test-id.util'
import Button from '../../button/button.component'
import { BUTTON_VARIANTS } from '../../button/button.constants'

import { styles as defaultStyles } from './tabs.styles'

const Tabs = ({
  tabs,
  defaultValue,
  activeTab,
  renderComponent: RenderComponent,
  setTabValue,
  customStyles,
  disabled,
  buttons,
}) => {
  const styles = customStyles ?? defaultStyles
  const [value, setValue] = useState(activeTab ? activeTab : defaultValue)
  const { t } = useTranslation(['common'])
  const handleChange = (_, newValue) => {
    setValue(newValue)
    if (setTabValue) {
      setTabValue(newValue)
    }
  }

  useEffect(() => {
    if (activeTab) {
      setValue(activeTab)
      if (setTabValue) {
        setTabValue(activeTab)
      }
    }
  }, [activeTab, setTabValue])

  return (
    <TabContext value={value} disabled={disabled}>
      <Box sx={styles.tabListContainer}>
        <TabList
          onChange={handleChange}
          aria-label={t('tabs')}
          sx={styles.tabList}
          TabIndicatorProps={{
            sx: styles.indicator,
            children: <span style={styles.indicator?.['& > span'] || {}} />,
          }}
          disabled={disabled}
        >
          {tabs &&
            tabs.map((tab) => (
              <Tab
                sx={[styles.tab]}
                key={tab.label}
                label={tab.label}
                value={tab.value}
                data-testid={tab.testId ?? buildRowTestId('tabs', tab.value)}
                disabled={disabled}
              />
            ))}
        </TabList>
        {buttons && (
          <Box sx={styles.buttonsContainer}>
            {buttons.map((button) => (
              <Button
                variant={BUTTON_VARIANTS.ICON}
                icon={button.icon}
                label={button.label}
                stroke
                onClick={button.action}
                key={button.key}
              />
            ))}
          </Box>
        )}
      </Box>
      {tabs &&
        tabs.map((tab) => {
          const Content = tab.content
          return (
            <TabPanel key={tab.label} value={tab.value} sx={styles.tabPanel}>
              {value === activeTab ? (
                <RenderComponent />
              ) : typeof Content === 'function' ? (
                value === tab.value ? (
                  <Content />
                ) : null
              ) : (
                Content
              )}
            </TabPanel>
          )
        })}
    </TabContext>
  )
}

Tabs.propTypes = {
  tabs: PropTypes.array.isRequired,
  defaultValue: PropTypes.string.isRequired,
  activeTab: PropTypes.string,
  renderComponent: PropTypes.node,
  setTabValue: PropTypes.func,
  customStyles: PropTypes.object,
  disabled: PropTypes.bool,
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.elementType.isRequired,
      action: PropTypes.func.isRequired,
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
}

export default Tabs
